import { useCallback, useId, useState, type FormEvent } from 'react';
import {
  KEYCLOAK_CLIENT_ROLES,
  resolveKeycloakClientRoleFromNames,
  type KeycloakClientRole,
} from '../../auth/clientRoles';
import { useProfilePhotoFormState } from '../../features/user/useProfilePhotoFormState';
import {
  useDeleteTenantUserPhotoMutation,
  useSetTenantUserRolesMutation,
  useUpdateTenantUserMutation,
  useUploadTenantUserPhotoMutation,
} from '../../features/tenant/mutations/useTenantUserMutations';
import { useTenantUserRolesQuery } from '../../features/tenant/queries/useTenantUserRolesQuery';
import { downloadTenantUserPhoto } from '../../features/tenant/downloadTenantUserPhoto';
import { useI18n } from '../../i18n/useI18n';
import type { CurrentUserProfile } from '../../types/user';
import { isValidEmailFormat } from '../../utils/formUtils';
import { unixMillisToDateInput } from '../../utils/dateTimeUtils';
import { submitTenantUserDetailsForm } from './submitTenantUserDetailsForm';
import {
  isTenantUserDetailsDirty,
  isTenantUserDetailsSaveDisabled,
  isTenantUserValidToBeforeValidFrom,
} from './tenantUserDetailsFormHelpers';

/** Org admins manage tenant membership roles; LAC_ADMIN is shown when already assigned. */
const TENANT_EDITABLE_ROLES: readonly KeycloakClientRole[] = [
  'ADMIN',
  'MEMBER',
];

export type UseTenantUserDetailsFormArgs = {
  profile: CurrentUserProfile;
  userId: string;
};

export function useTenantUserDetailsForm({
  profile,
  userId,
}: UseTenantUserDetailsFormArgs) {
  const { translate } = useI18n();
  const updateUserMutation = useUpdateTenantUserMutation();
  const uploadPhotoMutation = useUploadTenantUserPhotoMutation();
  const deletePhotoMutation = useDeleteTenantUserPhotoMutation();
  const setUserRolesMutation = useSetTenantUserRolesMutation();
  const rolesQuery = useTenantUserRolesQuery(userId);
  const loadedRole = resolveKeycloakClientRoleFromNames(rolesQuery.data);

  const photoFieldId = useId();
  const roleFieldId = useId();
  const [photoReloadKey, setPhotoReloadKey] = useState(0);

  const loadPhoto = useCallback(
    () => downloadTenantUserPhoto(userId),
    [userId],
  );

  const {
    previewSrc: photoDisplaySrc,
    photoError,
    photoChanged,
    pendingFile,
    removePhoto,
    handlePhotoChange,
    handleRemovePhoto,
    resetPhotoEdits,
  } = useProfilePhotoFormState({
    loadPhoto,
    translate,
    invalidTypeKey: 'organisationManagement.users.details.photoInvalidType',
    tooLargeKey: 'organisationManagement.users.details.photoTooLarge',
    reloadKey: photoReloadKey,
  });

  const username = profile.username ?? '';
  const [email, setEmail] = useState(profile.email ?? '');
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [lastName, setLastName] = useState(profile.lastName ?? '');
  const [roleOverride, setRoleOverride] = useState<KeycloakClientRole | null>(
    null,
  );
  const [validFrom, setValidFrom] = useState(
    unixMillisToDateInput(profile.validFrom),
  );
  const [validTo, setValidTo] = useState(
    unixMillisToDateInput(profile.validTo),
  );

  const role = roleOverride ?? loadedRole;
  const isPhotoBusy =
    uploadPhotoMutation.isPending || deletePhotoMutation.isPending;
  const isBusy =
    updateUserMutation.isPending ||
    setUserRolesMutation.isPending ||
    isPhotoBusy;
  const isSubmitting =
    updateUserMutation.isPending || setUserRolesMutation.isPending;
  const isRoleLoading = rolesQuery.isLoading;
  const isRoleLoadError = rolesQuery.isError;

  const roleOptions = (
    loadedRole === 'LAC_ADMIN' ? KEYCLOAK_CLIENT_ROLES : TENANT_EDITABLE_ROLES
  ).map((value) => ({
    value,
    label: translate(`organisationManagement.users.roles.${value}`),
  }));

  const trimmedEmail = email.trim();
  const isEmailInvalid =
    trimmedEmail !== '' && !isValidEmailFormat(trimmedEmail);
  const trimmedUsername = username.trim();
  const isValidToBeforeValidFrom = isTenantUserValidToBeforeValidFrom(
    validFrom,
    validTo,
  );

  const isDirty = isTenantUserDetailsDirty({
    profile,
    email,
    firstName,
    lastName,
    validFrom,
    validTo,
    roleOverride,
    loadedRole,
    photoChanged,
    photoError,
  });

  const isSaveDisabled = isTenantUserDetailsSaveDisabled({
    isDirty,
    isBusy,
    isRoleLoading,
    isRoleLoadError,
    email,
    username,
    firstName,
    lastName,
    validFrom,
    validTo,
    photoError,
  });

  const handleCancel = () => {
    setEmail(profile.email ?? '');
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setRoleOverride(null);
    setValidFrom(unixMillisToDateInput(profile.validFrom));
    setValidTo(unixMillisToDateInput(profile.validTo));
    resetPhotoEdits();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }

    submitTenantUserDetailsForm({
      translate,
      userId,
      profile,
      trimmedUsername,
      trimmedEmail,
      firstName,
      lastName,
      validFrom,
      validTo,
      role,
      loadedRole,
      isRoleLoadError,
      rolesData: rolesQuery.data,
      photoChanged,
      pendingFile,
      removePhoto,
      updateUserMutation,
      setUserRolesMutation,
      uploadPhotoMutation,
      deletePhotoMutation,
      bumpPhotoReloadKey: () => {
        setPhotoReloadKey((key) => key + 1);
      },
    });
  };

  return {
    translate,
    photoFieldId,
    roleFieldId,
    photoDisplaySrc,
    photoError,
    handlePhotoChange,
    handleRemovePhoto,
    username,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    role,
    setRoleOverride,
    validFrom,
    setValidFrom,
    validTo,
    setValidTo,
    isPhotoBusy,
    isBusy,
    isSubmitting,
    isRoleLoading,
    isRoleLoadError,
    roleOptions,
    isEmailInvalid,
    isValidToBeforeValidFrom,
    isDirty,
    isSaveDisabled,
    handleCancel,
    handleSubmit,
  };
}
